import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TextField } from '../components/ui-kit';
import { resetPassword } from '../api/auth';

// Публичная страница — переход по ссылке из письма сброса пароля
// (см. auth-user-services/src/services/passwordReset.js buildLink()).
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('form'); // form | success
  const [showPassword, setShowPassword] = useState(false);
  // returnUrl — откуда пришёл запрос на сброс (см. VerifyEmail.js — тот же
  // приём для email-подтверждения, auth-user-services/src/services/
  // passwordReset.js passwordResetRequestDomain).
  const [returnUrl, setReturnUrl] = useState(null);

  const formik = useFormik({
    initialValues: { password: '', passwordConfirm: '' },
    validationSchema: Yup.object({
      password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
      passwordConfirm: Yup.string()
        .required('Please confirm your password')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!token) {
        toast.error('This reset link is missing required parameters.');
        setSubmitting(false);
        return;
      }
      try {
        const result = await resetPassword(token, values.password);
        setReturnUrl(result?.returnUrl || null);
        setStatus('success');
      } catch (error) {
        toast.error(error.response?.data?.message || 'This reset link is invalid or has expired.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, maxWidth: 420, width: '100%' }}>
          {status === 'success' ? (
            <>
              <Typography variant="h4" component="h1" gutterBottom color="success.main">
                Password updated
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Your password has been reset. You can now sign in with your new password.
              </Typography>
              {returnUrl ? (
                <>
                  <Button variant="contained" color="primary" fullWidth component="a" href={returnUrl}>
                    Return to {new URL(returnUrl).hostname.replace(/^www\./, '')}
                  </Button>
                  <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                    Or sign in here
                  </Button>
                </>
              ) : (
                <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/login')}>
                  Go to sign in
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography variant="h5" component="h1" gutterBottom>
                Set a new password
              </Typography>
              <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ textAlign: 'left', mt: 2 }}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  required
                  fullWidth
                  name="passwordConfirm"
                  label="Confirm new password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formik.values.passwordConfirm}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.passwordConfirm && Boolean(formik.errors.passwordConfirm)}
                  helperText={formik.touched.passwordConfirm && formik.errors.passwordConfirm}
                  sx={{ mb: 3 }}
                />
                <Button type="submit" variant="contained" color="primary" fullWidth disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? <CircularProgress size={24} /> : 'Reset password'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
